// The drawn group digits: the seven-segment alphabets of the `digits` field
// (R427, R833, ui-map-interface.md §6.1).
//
// The control-group badge is one digit at the bottom-left corner of an own
// hull that holds a group. The AUTO value `mono` sets that digit in the
// vendored face (R427). The four other values DRAW it, out of bars on a
// grid, and they come from mockup round twenty-two, cards 02, 03, 04 and 06
// (`archive/mockups/022-group-digits.html`).
//
// **The four share one alphabet, and only the 1 separates them.** The
// digits 2 to 5 are the same bars in every value. The card pitched the 1 as
// the question the round was about: a bare stem, a stem under a flag, a
// stem between a flag and a foot, or the flag alphabet with round caps.
//
// | value           | card | the 1                   | caps  |
// | --------------- | ---- | ----------------------- | ----- |
// | `seg-stem`      | 02   | a stem                  | butt  |
// | `seg-flag`      | 03   | a stem under a flag     | butt  |
// | `seg-flag-foot` | 04   | a flag, a stem, a foot  | butt  |
// | `seg-round`     | 06   | a stem under a flag     | round |
//
// **The grid is data and the joins are shape.** `FLEET_DIGITS` holds the
// box, the bar, the flag and the inset of the 3. This file holds which of
// the grid nodes each digit joins, and a node is named and never spelled as
// a coordinate, so no figure of the grid is a literal here.
//
// This is a leaf (`leaves.test.ts`): it draws through `MarkGraphics` and
// imports no engine. `fast/gfx/atlas.ts` bakes one cell for each digit of
// the value the reader's Marks theme picked, and `fast/layers/fleets.ts`
// draws that cell.

import { FLEET_DIGITS } from '../gen/presentation';
import type { MarkGraphics } from './marks';
import type { DigitStyle } from '../ui/settings';

/** The grid figures the alphabet reads. The tests hand their own set. */
export type DigitFigures = typeof FLEET_DIGITS;

/** The digits a badge holds: one control group each. Six since R836. */
export type DigitValue = 1 | 2 | 3 | 4 | 5 | 6;

/** How a value draws the 1 (cards 02, 03 and 04). */
export type OneShape = 'stem' | 'flag' | 'flagfoot';

/** What a seven-segment value states of itself. */
export interface SegOpts {
  one: OneShape;
  /** Round caps, which is card 06 over the card 03 alphabet. */
  round: boolean;
}

/** One value of the `digits` field: the vendored face, or an alphabet. */
export type DigitStyleDef = { kind: 'font' } | ({ kind: 'seg' } & SegOpts);

/**
 * Every value of the `digits` field (R833).
 *
 * `mono` is the AUTO value and takes the font path of R427. The four others
 * draw, and `seg-round` is the `seg-flag` alphabet with round caps.
 */
export const DIGIT_STYLE_DEFS: Record<DigitStyle, DigitStyleDef> = {
  mono: { kind: 'font' },
  'seg-stem': { kind: 'seg', one: 'stem', round: false },
  'seg-flag': { kind: 'seg', one: 'flag', round: false },
  'seg-flag-foot': { kind: 'seg', one: 'flagfoot', round: false },
  'seg-round': { kind: 'seg', one: 'flag', round: true },
};

/**
 * The nodes across the box: the left edge, the middle, the right edge, and
 * the inset left of the 3's middle bar.
 */
export type XNode = 'l' | 'm' | 'r' | 'i';

/** The nodes down the box: the top, the middle and the bottom. */
export type YNode = 't' | 'c' | 'b';

/** One bar, from one node of the grid to another. */
export type Seg = readonly [XNode, YNode, XNode, YNode];

/**
 * Which bars each digit joins (the `segAlphabet` of card 01).
 *
 * The 1 is the bare stem here. Its foot and its flag are not shape shared
 * with another digit: they are what a value states, so `segDigit` adds
 * them.
 */
export const SEGMENTS: Record<DigitValue, readonly Seg[]> = {
  1: [['m', 't', 'm', 'b']],
  2: [
    ['l', 't', 'r', 't'], ['r', 't', 'r', 'c'], ['l', 'c', 'r', 'c'],
    ['l', 'c', 'l', 'b'], ['l', 'b', 'r', 'b'],
  ],
  3: [
    ['l', 't', 'r', 't'], ['r', 't', 'r', 'c'], ['i', 'c', 'r', 'c'],
    ['r', 'c', 'r', 'b'], ['l', 'b', 'r', 'b'],
  ],
  4: [
    ['l', 't', 'l', 'c'], ['r', 't', 'r', 'c'], ['l', 'c', 'r', 'c'],
    ['r', 'c', 'r', 'b'],
  ],
  5: [
    ['l', 't', 'r', 't'], ['l', 't', 'l', 'c'], ['l', 'c', 'r', 'c'],
    ['r', 'c', 'r', 'b'], ['l', 'b', 'r', 'b'],
  ],
  // The 6 is the 5 with the lower-left bar closed (R836, which took the
  // control groups to six slots). It is the seven-segment 6 every drawn
  // alphabet states, so no value of the `digits` field needs a rule.
  6: [
    ['l', 't', 'r', 't'], ['l', 't', 'l', 'c'], ['l', 'c', 'r', 'c'],
    ['l', 'c', 'l', 'b'], ['r', 'c', 'r', 'b'], ['l', 'b', 'r', 'b'],
  ],
};

/** The foot of the 1, which cards 01 and 04 draw. */
const FOOT: Seg = ['l', 'b', 'r', 'b'];

/** The bars of the 1 of card 04: the stem and its foot. */
const ONE_WITH_FOOT: readonly Seg[] = [...SEGMENTS[1], FOOT];

/** The ink of a drawn digit. The quad carries the tint and the alpha. */
const WHITE = 0xffffff; // mask

/**
 * Where the nodes of the grid stand, in map px around the centre of the box.
 *
 * Every end of a bar is held half a bar inside the box, the way the card
 * holds it, so the INK of a digit measures the box exactly: `h` px down and
 * `box_w_units / box_h_units · h` px across.
 */
function gridOf(h: number, f: DigitFigures) {
  const s = h / f.boxHUnits;
  const hw = (f.barUnits * s) / 2;
  const x = {
    l: -(f.boxWUnits / 2) * s + hw,
    m: 0,
    r: (f.boxWUnits / 2) * s - hw,
    i: -(f.boxWUnits / 2) * s + hw + f.threeInsetUnits * s,
  };
  const y = { t: -(f.boxHUnits / 2) * s + hw, c: 0, b: (f.boxHUnits / 2) * s - hw };
  return { s, x, y };
}

/** The width of the box one digit stands in, in map px. */
export function segBoxWidth(h: number, f: DigitFigures = FLEET_DIGITS): number {
  return (f.boxWUnits / f.boxHUnits) * h;
}

/**
 * Draws one digit of a seven-segment value, centred on the origin.
 *
 * `h` is the height of the box, which is the height of the ink. The digit
 * is painted WHITE in ONE stroke, because every bar of it carries the same
 * width and the same cap, and §10 asks a description to batch a shape of
 * one style rather than to paint it bar by bar.
 *
 * It returns the width of the box, so a caller that places a run of digits
 * reads the advance from the alphabet and states no second figure.
 */
export function segDigit(
  g: MarkGraphics, n: DigitValue, h: number, opts: SegOpts,
  f: DigitFigures = FLEET_DIGITS,
): number {
  const { s, x, y } = gridOf(h, f);
  const bars = n === 1 && opts.one === 'flagfoot' ? ONE_WITH_FOOT : SEGMENTS[n];
  for (const [ax, ay, bx, by] of bars) g.moveTo(x[ax], y[ay]).lineTo(x[bx], y[by]);
  // The flag falls away from the head of the stem, up and to the left, as a
  // printed 1 wears it. It is a diagonal, so it joins no node of the grid.
  if (n === 1 && (opts.one === 'flag' || opts.one === 'flagfoot')) {
    const len = f.flagLenUnits * s;
    g.moveTo(x.m, y.t).lineTo(x.m - len, y.t + len);
  }
  g.stroke({
    width: f.barUnits * s, color: WHITE,
    cap: opts.round ? 'round' : 'butt', join: 'miter',
  });
  return segBoxWidth(h, f);
}
